<template>
    <div
        class="hts-form-date form-control"
        :class="cColorSize">
        <label
            v-if="!onlyDatePicker && label"
            class="label"
            :for="name">
            <span class="label-text">
                {{ label }}<span
                    v-if="required"
                    class="text-error-500 ml-1">
                    *
                </span>
            </span>
        </label>
        <div class="min-w-full max-w-full relative">
            <date-picker
                :id="name"
                v-model:value="cValue"
                class="min-w-full max-w-full"
                :class="cColorSize"
                v-bind="pickProps"
                :input-class="inputClass" />
            <input
                :id="`${name || 'unknown'}-date-picker`"
                :model-value="cValue"
                :value="cValue"
                class="absolute bottom-0 left-0 w-full !h-1 text-transparent select-none bg-transparent border-none outline-0"
                :name="prop"
                :required="required" >
        </div>

        <span
            v-if="!onlyDatePicker && (errorMessage || description)"
            class="label">
            <span
                v-if="errorMessage"
                class="label-text-alt text-rose-500 text-no lowercase first-letter:capitalize">
                {{ errorMessage }}
            </span>
            <span
                v-if="description"
                class="label-text-alt">
                >{{ description }}
            </span>
        </span>
    </div>
</template>

<script lang="ts" setup>
import { useField, useFieldError } from 'vee-validate'
import DatePicker from './-date-picker'
import type { DateValue } from './-date-picker/type'
import { daisyuiColorSize } from '~/components/utils/daisyui'

const { $dayjs } = useNuxtApp()

const props = defineProps({
  modelValue: {
    type: [Number, String, Array, Date],
    required: true
  },
  type: {
    type: String,
    default: 'date'
  },
  label: String,
  placeholder: String,
  description: String,
  prop: String,
  name: String,
  rules: Object,
  color: String,
  size: String,
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  valueType: {
    type: String,
    default: 'YYYY-MM-DD'
  },
  format: {
    type: String,
    default: 'YYYY-MM-DD'
  },
  timeFormat: {
    type: String,
    default: ''
  },
  inputClass: {
    type: String,
    default: ''
  },
  onlyDatePicker: {
    type: Boolean,
    default: false
  },
  appendToBody: {
    type: Boolean,
    default: true
  },
  isShortcut: {
    type: Boolean,
    default: false
  },
  unixTime: {
    type: Boolean,
    default: false
  }
})
const { modelValue, type, prop, name, rules, format, disabled, required, readonly, timeFormat, unixTime } = toRefs(props)

const locked = computed(() => disabled.value || readonly.value)
const propKeys = [
  'type',
  'valueType',
  'appendToBody',
  'clearable',
  'confirm',
  'confirmText',
  'disabled',
  'disabledDate',
  'disabledTime',
  'editable',
  'format',
  'titleFormat',
  'formatter',
  'inputAttr',
  'inputClass',
  'lang',
  'onChange',
  'onClear',
  'onClose',
  'onConfirm',
  'onInputError',
  'onUpdate:open',
  'onUpdate:value',
  'open',
  'placeholder',
  'popupClass',
  'popupStyle',
  'prefixClass',
  'range',
  'renderInputText',
  'separator',
  'shortcuts',
  'isShortcut'
]
const open = ref<String | Boolean>('auto')
const yearFormats = ['YY', 'YYYY']
const monthFormats = ['M', 'MM', 'MMM', 'MMMM']

let tmp = ''
let a = ''
const cFormat = computed(() => {
  switch (type.value) {
  case 'datetime':
    tmp = ''
    if (!format.value.toLowerCase().includes('h')) {
      tmp += 'HH'
    }
    if (!format.value.includes('m')) {
      tmp += ':mm'
    }
    if (!format.value.toLowerCase().includes('s')) {
      tmp += ':ss'
    }

    if (timeFormat.value) {
      tmp = timeFormat.value
    }
    return format.value + ` ${tmp}`
  case 'time':
    a = ''
    if (!format.value.toLowerCase().includes('h')) {
      a += 'HH'
    }
    if (!format.value.includes('m')) {
      a += ':mm'
    }
    if (!format.value.toLowerCase().includes('s')) {
      a += ':ss'
    }
    return a
  case 'year':
    return yearFormats.includes(format.value) ? format.value : 'YYYY'
  case 'month':
    return monthFormats.includes(format.value) ? format.value : 'MM'
  case 'week':
    return 'YYYY-MM-DD'
  }
  return format.value
})
const attrs = useAttrs()
const pickProps = computed(() => {
  const res: { [key: string]: any } = {}
  const data = { ...attrs, ...props }
  propKeys.forEach((prop: string) => {
    if (Object.prototype.hasOwnProperty.call(data, prop)) {
      switch (prop) {
      case 'format':
      case 'valueType':
        res[prop] = cFormat.value
        break
      case 'isShortcut':
        if (data[prop]) {
          res.shortcuts = shortcutItems
        }
        break
      case 'disabled':
        res[prop] = locked.value
        break
      default:
        res[prop] = { ...data }[prop]
      }
    }
  })

  return res
})

const shortcutItems = [
  { text: $t('labels.toDay'), onClick: () => [new Date(), new Date()] },
  { text: $t('labels.last7day'), onClick: () => [getLastDay(6), new Date()] },
  { text: $t('labels.last30day'), onClick: () => [getLastDay(29), new Date()] },
  { text: $t('labels.thisMonth'), onClick: () => getDateThisMonth() },
  { text: $t('labels.lastMonth'), onClick: () => getDateLastMonth() },
  { text: $t('labels.thisYear'), onClick: () => getDateThisYear() }
]

const getDateThisYear = () => {
  const date = new Date()
  const startDateOfMonth = new Date(date.getFullYear(), 0, 1)
  const endDateOfMonth = new Date(date.getFullYear(), 12, 0)
  return [startDateOfMonth, endDateOfMonth]
}

const getDateLastMonth = () => {
  const date = new Date()
  const startDateOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1)
  const endDateOfMonth = new Date(date.getFullYear(), date.getMonth(), 0)
  return [startDateOfMonth, endDateOfMonth]
}

const getDateThisMonth = () => {
  const date = new Date()
  const startDateOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
  const endDateOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return [startDateOfMonth, endDateOfMonth]
}

const getLastDay = (n: number) => {
  const today = new Date()
  const daySeven = new Date(today)
  daySeven.setDate(daySeven.getDate() - n)
  return daySeven
}

const emits = defineEmits(['update:modelValue'])
const cColorSize = daisyuiColorSize('datepicker', props)

let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)
const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })

    errorMessage = useFieldError(cProp.value)
  }
}
watch(
  () => cProp.value,
  (val: any) => {
    setFields(val)
  }
)

watch(
  () => modelValue.value,
  (val: any) => {
    field?.setValue(val)
  }
)

onMounted(() => {
  setFields(cProp.value)
  nextTick(() => {
    field && field.setValue(modelValue.value)
  })
})

const cValue = computed<DateValue>({
  get () {
    if (unixTime.value) {
      if (!modelValue?.value || Array.isArray(modelValue?.value)) {
        return null
      }
      return $dayjs(Number(modelValue?.value)).format(cFormat.value)

      // return new Date(modelValue?.value).format(cFormat.value)
    }

    return type.value === 'year' && modelValue?.value ? modelValue.value.toString() : (modelValue?.value as DateValue)
  },
  set (val: any) {
    field?.setValue(val)
    if (unixTime.value) {
      emits('update:modelValue', Math.floor(new Date(val).getTime()))
    } else {
      emits('update:modelValue', type.value === 'year' ? parseInt(val) : val)
    }
  }
})
</script>
